import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Button, Stat, StatLabel, StatNumber, StatHelpText } from "@chakra-ui/react";
import { FaBuilding, FaDollarSign, FaLeaf, FaChartLine, FaIndustry, FaWarehouse } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SolarServicesCommercial: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/services/commercial"
        fallbackTitle="Commercial Solar Services"
        fallbackDescription="Large-scale solar installations for businesses and commercial properties"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 20 }}
        backgroundImage="url('https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Icon as={FaBuilding} boxSize={16} color="#C69214" />
            <Heading size="2xl" fontWeight="bold">
              Commercial Solar Solutions
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Reduce operating costs and showcase environmental leadership
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Business Benefits Grid */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Why Businesses Choose Solar</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="2xl">
            Strategic advantages that drive long-term value
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" _hover={{ borderColor: "#C69214", shadow: "lg" }} transition="all 0.3s">
            <Icon as={FaDollarSign} boxSize={12} color="#C69214" mb={4} />
            <Heading size="md" mb={3}>Immediate Cost Savings</Heading>
            <Text color="gray.600" fontSize="sm" mb={2}>
              Reduce electricity expenses by 30–70% depending on system size and usage profile
            </Text>
            <Text color="#141C49" fontWeight="bold" fontSize="sm">
              Average ROI: 3-5 years
            </Text>
          </Box>

          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" _hover={{ borderColor: "#C69214", shadow: "lg" }} transition="all 0.3s">
            <Icon as={FaChartLine} boxSize={12} color="#C69214" mb={4} />
            <Heading size="md" mb={3}>Tax Benefits</Heading>
            <Text color="gray.600" fontSize="sm" mb={2}>
              Instant asset write-off and accelerated depreciation available for eligible businesses
            </Text>
            <Text color="#141C49" fontWeight="bold" fontSize="sm">
              Speak with your accountant — conditions apply
            </Text>
          </Box>

          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" _hover={{ borderColor: "#C69214", shadow: "lg" }} transition="all 0.3s">
            <Icon as={FaLeaf} boxSize={12} color="#C69214" mb={4} />
            <Heading size="md" mb={3}>Brand Enhancement</Heading>
            <Text color="gray.600" fontSize="sm" mb={2}>
              Demonstrate sustainability commitment to clients and partners
            </Text>
            <Text color="#141C49" fontWeight="bold" fontSize="sm">
              ESG compliance support
            </Text>
          </Box>

          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" _hover={{ borderColor: "#C69214", shadow: "lg" }} transition="all 0.3s">
            <Icon as={FaIndustry} boxSize={12} color="#C69214" mb={4} />
            <Heading size="md" mb={3}>Energy Independence</Heading>
            <Text color="gray.600" fontSize="sm" mb={2}>
              Protection from rising grid electricity costs
            </Text>
            <Text color="#141C49" fontWeight="bold" fontSize="sm">
              Lock in energy costs for 25+ years
            </Text>
          </Box>

          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" _hover={{ borderColor: "#C69214", shadow: "lg" }} transition="all 0.3s">
            <Icon as={FaWarehouse} boxSize={12} color="#C69214" mb={4} />
            <Heading size="md" mb={3}>Property Value</Heading>
            <Text color="gray.600" fontSize="sm" mb={2}>
              Increase asset value with income-generating infrastructure
            </Text>
            <Text color="#141C49" fontWeight="bold" fontSize="sm">
              Average 4% property value increase
            </Text>
          </Box>

          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" _hover={{ borderColor: "#C69214", shadow: "lg" }} transition="all 0.3s">
            <Icon as={FaBuilding} boxSize={12} color="#C69214" mb={4} />
            <Heading size="md" mb={3}>Scalable Solutions</Heading>
            <Text color="gray.600" fontSize="sm" mb={2}>
              Systems designed to grow with your business needs
            </Text>
            <Text color="#141C49" fontWeight="bold" fontSize="sm">
              10kW to 1MW+ capacity
            </Text>
          </Box>
        </SimpleGrid>
      </Container>

      {/* ROI Calculator Teaser */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Heading size="xl">Calculate Your Savings</Heading>
              <Text fontSize="lg" color="gray.600">
                See how much your business can save with commercial solar
              </Text>
              <SimpleGrid columns={2} spacing={6} w="full">
                <Stat>
                  <StatLabel>Average System Size</StatLabel>
                  <StatNumber color="#141C49">100kW</StatNumber>
                  <StatHelpText>Small-Medium Business</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Annual Savings</StatLabel>
                  <StatNumber color="#C69214">$25,000+</StatNumber>
                  <StatHelpText>Per year</StatHelpText>
                </Stat>
              </SimpleGrid>
              <Button bg="#141C49" color="white" size="lg" _hover={{ bg: "#a07e10" }} transition="all 0.2s">
                Get Custom ROI Report
              </Button>
            </VStack>

            <Box
              bg="white"
              p={8}
              borderRadius="xl"
              shadow="xl"
              border="2px solid"
              borderColor="gray.200"
            >
              <VStack spacing={4} align="stretch">
                <Heading size="md" textAlign="center" color="#141C49">
                  ROI Calculator
                </Heading>
                <Box
                  h={200}
                  bg="gray.50"
                  borderRadius="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap={3}
                  p={6}
                >
                  <Text color="gray.600" fontSize="sm" textAlign="center">
                    Every commercial installation is different. Request a custom ROI report based on your actual energy usage and site conditions.
                  </Text>
                </Box>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Case Studies Placeholder */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Example Project Scale</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="2xl">
            Indicative system sizes and savings — contact us for a custom commercial assessment
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          <Box
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            _hover={{ shadow: "lg", borderColor: "#C69214" }}
            transition="all 0.3s"
          >
            <Box h={150} bg="gray.100" display="flex" alignItems="center" justifyContent="center">
              <Icon as={FaWarehouse} boxSize={16} color="#C69214" />
            </Box>
            <Box p={6}>
              <Heading size="sm" mb={2}>Manufacturing Facility</Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>
                250kW system installed | $60k annual savings
              </Text>
            </Box>
          </Box>

          <Box
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            _hover={{ shadow: "lg", borderColor: "#C69214" }}
            transition="all 0.3s"
          >
            <Box h={150} bg="gray.100" display="flex" alignItems="center" justifyContent="center">
              <Icon as={FaBuilding} boxSize={16} color="#C69214" />
            </Box>
            <Box p={6}>
              <Heading size="sm" mb={2}>Office Complex</Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>
                150kW system installed | $35k annual savings
              </Text>
            </Box>
          </Box>

          <Box
            borderWidth={1}
            borderColor="gray.200"
            borderRadius="lg"
            overflow="hidden"
            _hover={{ shadow: "lg", borderColor: "#C69214" }}
            transition="all 0.3s"
          >
            <Box h={150} bg="gray.100" display="flex" alignItems="center" justifyContent="center">
              <Icon as={FaIndustry} boxSize={16} color="#C69214" />
            </Box>
            <Box p={6}>
              <Heading size="sm" mb={2}>Retail Center</Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>
                100kW system installed | $22k annual savings
              </Text>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg="gray.800" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center" color="white">
            <Heading size="xl">Ready to Reduce Operating Costs?</Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl">
              Schedule a free energy audit and receive a detailed commercial solar proposal
            </Text>
            <Button size="lg" bg="white" color="#141C49" _hover={{ bg: "#a07e10", color: "white" }} transition="all 0.2s">
              Request Commercial Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarServicesCommercial;
